import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import SaveCalculationDialog from '@/components/SaveCalculationDialog';
import { 
  ArrowLeft, 
  Calculator, 
  DollarSign,
  Percent,
  Home,
  PiggyBank,
  TrendingUp,
  Info,
  Download,
  Building2,
  Save
} from 'lucide-react';

const MortgageCalculator = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  
  // Input states
  const [homePrice, setHomePrice] = useState(400000);
  const [downPayment, setDownPayment] = useState(80000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [loanTerm, setLoanTerm] = useState(30);
  const [interestRate, setInterestRate] = useState(6.5);
  const [propertyTax, setPropertyTax] = useState(5000); // Changed to dollar amount
  const [homeInsurance, setHomeInsurance] = useState(1500);
  const [pmi, setPmi] = useState(0.5);
  const [hoaFee, setHoaFee] = useState(0); // New HOA fee (monthly)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Calculated results
  const [results, setResults] = useState(null);

  // Update down payment when percentage changes
  useEffect(() => {
    setDownPayment(Math.round(homePrice * (downPaymentPercent / 100)));
  }, [homePrice, downPaymentPercent]);

  // Calculate mortgage
  useEffect(() => {
    calculateMortgage();
  }, [homePrice, downPayment, loanTerm, interestRate, propertyTax, homeInsurance, pmi, hoaFee]);

  const calculateMortgage = () => {
    const principal = homePrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    // Monthly mortgage payment (P&I)
    let monthlyPayment = 0;
    if (monthlyRate > 0) {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                       (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    } else {
      monthlyPayment = principal / numberOfPayments;
    }
    
    // Monthly property tax (now from dollar amount)
    const monthlyPropertyTax = propertyTax / 12;
    
    // Monthly home insurance
    const monthlyInsurance = homeInsurance / 12;
    
    // Monthly PMI (if down payment < 20%)
    const monthlyPmi = downPaymentPercent < 20 ? (principal * (pmi / 100)) / 12 : 0;
    
    // Monthly HOA
    const monthlyHoa = hoaFee;
    
    // Total monthly payment
    const totalMonthly = monthlyPayment + monthlyPropertyTax + monthlyInsurance + monthlyPmi + monthlyHoa;
    
    // Total interest over life of loan
    const totalPayments = monthlyPayment * numberOfPayments;
    const totalInterest = totalPayments - principal;
    
    // Payoff date
    const payoffDate = new Date();
    payoffDate.setMonth(payoffDate.getMonth() + numberOfPayments);
    
    setResults({
      monthlyPayment: monthlyPayment,
      monthlyPropertyTax: monthlyPropertyTax,
      monthlyInsurance: monthlyInsurance,
      monthlyPmi: monthlyPmi,
      monthlyHoa: monthlyHoa,
      totalMonthly: totalMonthly,
      principal: principal,
      totalInterest: totalInterest,
      totalPayments: totalPayments,
      payoffDate: payoffDate,
      numberOfPayments: numberOfPayments
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyDetailed = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Calculate pie chart percentages
  const getPieChartData = () => {
    if (!results) return { pi: 70, tax: 15, ins: 10, pmi: 5, hoa: 0 };
    const total = results.totalMonthly;
    return {
      pi: (results.monthlyPayment / total * 100).toFixed(0),
      tax: (results.monthlyPropertyTax / total * 100).toFixed(0),
      ins: (results.monthlyInsurance / total * 100).toFixed(0),
      pmi: (results.monthlyPmi / total * 100).toFixed(0),
      hoa: (results.monthlyHoa / total * 100).toFixed(0)
    };
  };

  const pieData = getPieChartData();

  // Generate PDF Report
  const generatePdf = async () => {
    setIsGeneratingPdf(true);
    
    try {
      // Create a simple HTML document for PDF
      const today = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Mortgage Calculator Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px; }
            .header h1 { color: #3b82f6; margin: 0; font-size: 28px; }
            .header p { color: #666; margin-top: 5px; }
            .section { margin-bottom: 25px; }
            .section-title { font-size: 18px; font-weight: bold; color: #3b82f6; margin-bottom: 15px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
            .highlight-box { background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; padding: 25px; border-radius: 12px; text-align: center; margin-bottom: 25px; }
            .highlight-box .label { font-size: 14px; opacity: 0.9; }
            .highlight-box .amount { font-size: 36px; font-weight: bold; margin-top: 5px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
            .item-label { color: #666; }
            .item-value { font-weight: 600; }
            .item-value.primary { color: #3b82f6; }
            .item-value.danger { color: #ef4444; }
            .breakdown-item { display: flex; align-items: center; gap: 10px; padding: 8px 0; }
            .breakdown-dot { width: 12px; height: 12px; border-radius: 3px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #999; font-size: 12px; }
            .disclaimer { background: #f9fafb; padding: 15px; border-radius: 8px; font-size: 11px; color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Mortgage Calculator Report</h1>
            <p>Generated on ${today}</p>
          </div>
          
          <div class="highlight-box">
            <div class="label">Estimated Monthly Payment</div>
            <div class="amount">${formatCurrencyDetailed(results?.totalMonthly || 0)}</div>
          </div>
          
          <div class="grid">
            <div class="section">
              <div class="section-title">Loan Details</div>
              <div class="item">
                <span class="item-label">Home Price</span>
                <span class="item-value">${formatCurrency(homePrice)}</span>
              </div>
              <div class="item">
                <span class="item-label">Down Payment</span>
                <span class="item-value">${formatCurrency(downPayment)} (${downPaymentPercent}%)</span>
              </div>
              <div class="item">
                <span class="item-label">Loan Amount</span>
                <span class="item-value primary">${formatCurrency(results?.principal || 0)}</span>
              </div>
              <div class="item">
                <span class="item-label">Interest Rate</span>
                <span class="item-value">${interestRate}%</span>
              </div>
              <div class="item">
                <span class="item-label">Loan Term</span>
                <span class="item-value">${loanTerm} years</span>
              </div>
              <div class="item">
                <span class="item-label">Payoff Date</span>
                <span class="item-value">${results?.payoffDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) || '-'}</span>
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Monthly Breakdown</div>
              <div class="item">
                <span class="item-label">Principal & Interest</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyPayment || 0)}</span>
              </div>
              <div class="item">
                <span class="item-label">Property Tax</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyPropertyTax || 0)}</span>
              </div>
              <div class="item">
                <span class="item-label">Home Insurance</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyInsurance || 0)}</span>
              </div>
              ${results?.monthlyPmi > 0 ? `
              <div class="item">
                <span class="item-label">PMI</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyPmi || 0)}</span>
              </div>
              ` : ''}
              ${results?.monthlyHoa > 0 ? `
              <div class="item">
                <span class="item-label">HOA Fee</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyHoa || 0)}</span>
              </div>
              ` : ''}
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Total Cost Analysis</div>
            <div class="item">
              <span class="item-label">Total of ${results?.numberOfPayments || 0} Payments</span>
              <span class="item-value">${formatCurrency(results?.totalPayments || 0)}</span>
            </div>
            <div class="item">
              <span class="item-label">Total Interest Paid</span>
              <span class="item-value danger">${formatCurrency(results?.totalInterest || 0)}</span>
            </div>
          </div>
          
          <div class="disclaimer">
            <strong>Disclaimer:</strong> This is an estimate only. Actual payments may vary based on taxes, insurance rates, and other factors. 
            Please consult with a mortgage professional for accurate figures. PMI is estimated at ${pmi}% annually and may vary by lender.
          </div>
          
          <div class="footer">
            Generated by ListingPro AI Mortgage Calculator
          </div>
        </body>
        </html>
      `;
      
      // Create a blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Report generated! Use your browser\'s print dialog to save as PDF.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Prepare calculation data for saving
  const getCalculationData = () => {
    if (!results) return null;
    return {
      home_price: homePrice,
      down_payment: downPayment,
      down_payment_percent: downPaymentPercent,
      loan_term: loanTerm,
      interest_rate: interestRate,
      property_tax: propertyTax,
      home_insurance: homeInsurance,
      hoa_fee: hoaFee,
      pmi: pmi,
      monthly_payment: results.totalMonthly,
      monthly_pi: results.monthlyPayment,
      monthly_tax: results.monthlyPropertyTax,
      monthly_insurance: results.monthlyInsurance,
      monthly_pmi: results.monthlyPmi,
      monthly_hoa: results.monthlyHoa,
      total_interest: results.totalInterest,
      loan_amount: results.principal
    };
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="mortgage-calculator-page">
      <Navbar variant="app" />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                <Calculator className="text-primary" size={32} />
                Mortgage Calculator
              </h1>
              <p className="text-muted-foreground mt-1">Calculate monthly payments and total costs</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowSaveDialog(true)} 
              disabled={!results}
              className="gap-2"
              data-testid="save-calculation-btn"
            >
              <Save size={18} />
              Save & Assign
            </Button>
            <Button 
              onClick={generatePdf} 
              disabled={isGeneratingPdf || !results}
              className="gap-2"
              data-testid="download-pdf-btn"
            >
              <Download size={18} />
              {isGeneratingPdf ? 'Generating...' : 'Download Report'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" ref={reportRef}>
          {/* Input Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Home Price & Down Payment */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Home className="text-primary" size={20} />
                  Property Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Home Price */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Home Price</Label>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(homePrice)}</span>
                  </div>
                  <Slider
                    value={[homePrice]}
                    onValueChange={(v) => setHomePrice(v[0])}
                    min={50000}
                    max={10000000}
                    step={10000}
                    className="py-2"
                    data-testid="home-price-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$50,000</span>
                    <span>$10,000,000</span>
                  </div>
                </div>

                {/* Down Payment */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Down Payment</Label>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-primary">{formatCurrency(downPayment)}</span>
                      <span className="text-sm text-muted-foreground ml-2">({downPaymentPercent}%)</span>
                    </div>
                  </div>
                  <Slider
                    value={[downPaymentPercent]}
                    onValueChange={(v) => setDownPaymentPercent(v[0])}
                    min={0}
                    max={50}
                    step={1}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0%</span>
                    <span>50%</span>
                  </div>
                  {downPaymentPercent < 20 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Info size={12} />
                      PMI required for down payments under 20%
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Loan Details */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PiggyBank className="text-primary" size={20} />
                  Loan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Interest Rate */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Interest Rate (APR)</Label>
                    <span className="text-2xl font-bold text-primary">{interestRate}%</span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={(v) => setInterestRate(v[0])}
                    min={1}
                    max={12}
                    step={0.125}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1%</span>
                    <span>12%</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Loan Term</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[15, 20, 30].map((term) => (
                      <Button
                        key={term}
                        variant={loanTerm === term ? "default" : "outline"}
                        onClick={() => setLoanTerm(term)}
                        className="w-full"
                      >
                        {term} Years
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Costs */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="text-primary" size={20} />
                  Additional Costs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Property Tax ($/year)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                      <Input
                        type="number"
                        value={propertyTax}
                        onChange={(e) => setPropertyTax(parseFloat(e.target.value) || 0)}
                        step={100}
                        min={0}
                        className="pl-8"
                        data-testid="property-tax-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Home Insurance ($/year)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                      <Input
                        type="number"
                        value={homeInsurance}
                        onChange={(e) => setHomeInsurance(parseFloat(e.target.value) || 0)}
                        step={100}
                        min={0}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Building2 size={14} className="text-muted-foreground" />
                      HOA Fee ($/month)
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                      <Input
                        type="number"
                        value={hoaFee}
                        onChange={(e) => setHoaFee(parseFloat(e.target.value) || 0)}
                        step={25}
                        min={0}
                        className="pl-8"
                        data-testid="hoa-fee-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">PMI (%/year)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={pmi}
                        onChange={(e) => setPmi(parseFloat(e.target.value) || 0)}
                        step={0.1}
                        min={0}
                        max={2}
                        className="pr-8"
                        disabled={downPaymentPercent >= 20}
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    </div>
                    {downPaymentPercent >= 20 && (
                      <p className="text-xs text-green-600">No PMI required with 20%+ down</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Monthly Payment */}
            <Card className="bg-gradient-to-br from-primary to-primary/80 text-white">
              <CardContent className="pt-6">
                <p className="text-primary-foreground/80 text-sm font-medium mb-1">Monthly Payment</p>
                <p className="text-4xl font-bold mb-4" data-testid="monthly-payment-display">
                  {results ? formatCurrencyDetailed(results.totalMonthly) : '$0.00'}
                </p>
                
                {/* Payment Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-primary-foreground/80">Principal & Interest</span>
                    <span className="font-medium">{results ? formatCurrencyDetailed(results.monthlyPayment) : '$0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-foreground/80">Property Tax</span>
                    <span className="font-medium">{results ? formatCurrencyDetailed(results.monthlyPropertyTax) : '$0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-foreground/80">Home Insurance</span>
                    <span className="font-medium">{results ? formatCurrencyDetailed(results.monthlyInsurance) : '$0'}</span>
                  </div>
                  {results?.monthlyPmi > 0 && (
                    <div className="flex justify-between">
                      <span className="text-primary-foreground/80">PMI</span>
                      <span className="font-medium">{formatCurrencyDetailed(results.monthlyPmi)}</span>
                    </div>
                  )}
                  {results?.monthlyHoa > 0 && (
                    <div className="flex justify-between">
                      <span className="text-primary-foreground/80">HOA Fee</span>
                      <span className="font-medium">{formatCurrencyDetailed(results.monthlyHoa)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Loan Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Loan Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Home Price</span>
                  <span className="font-semibold">{formatCurrency(homePrice)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Down Payment</span>
                  <span className="font-semibold">{formatCurrency(downPayment)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Loan Amount</span>
                  <span className="font-semibold text-primary">{results ? formatCurrency(results.principal) : '$0'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Total Interest</span>
                  <span className="font-semibold text-red-500">{results ? formatCurrency(results.totalInterest) : '$0'}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Total P&I Payments</span>
                  <span className="font-semibold">{results ? formatCurrency(results.totalPayments) : '$0'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Payoff Date</span>
                  <span className="font-semibold">
                    {results ? results.payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Payment Breakdown Visual */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Payment Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-primary"></div>
                    <span className="text-sm flex-1">Principal & Interest</span>
                    <span className="text-sm font-medium">{pieData.pi}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-blue-500"></div>
                    <span className="text-sm flex-1">Property Tax</span>
                    <span className="text-sm font-medium">{pieData.tax}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span className="text-sm flex-1">Home Insurance</span>
                    <span className="text-sm font-medium">{pieData.ins}%</span>
                  </div>
                  {results?.monthlyPmi > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-amber-500"></div>
                      <span className="text-sm flex-1">PMI</span>
                      <span className="text-sm font-medium">{pieData.pmi}%</span>
                    </div>
                  )}
                  {results?.monthlyHoa > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-purple-500"></div>
                      <span className="text-sm flex-1">HOA Fee</span>
                      <span className="text-sm font-medium">{pieData.hoa}%</span>
                    </div>
                  )}
                </div>
                
                {/* Simple bar visualization */}
                <div className="mt-4 h-4 rounded-full overflow-hidden flex">
                  <div className="bg-primary" style={{ width: `${pieData.pi}%` }}></div>
                  <div className="bg-blue-500" style={{ width: `${pieData.tax}%` }}></div>
                  <div className="bg-green-500" style={{ width: `${pieData.ins}%` }}></div>
                  {results?.monthlyPmi > 0 && (
                    <div className="bg-amber-500" style={{ width: `${pieData.pmi}%` }}></div>
                  )}
                  {results?.monthlyHoa > 0 && (
                    <div className="bg-purple-500" style={{ width: `${pieData.hoa}%` }}></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Save Calculation Dialog */}
      <SaveCalculationDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        calculatorType="mortgage"
        calculationData={getCalculationData()}
        onSaved={() => toast.success('You can now view this calculation on the listing page')}
      />
    </div>
  );
};

export default MortgageCalculator;
